import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { Alert, Button, Input, Label, Col, Row } from 'reactstrap';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { ProductData, PackageData } from 'types/Product';
import { getProducts } from 'helpers/API Calls/Operations/Products';
import { checkAvailability } from 'helpers/API Calls/Operations/booking';
import { FormValues, ProductWithQuantity, SelectOption } from './types';
import { useDispatch } from 'react-redux';
import { selectBookingDate, selectCartItems, selectIsDiscountedBooking, selectIsEdit, setBookingDate, setItems, setIsPackageItem, setPackageData, setCleaningDeposit} from 'slices/booking/cartSlice';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { getPackages } from 'helpers/API Calls/Operations/packages';

interface PackageSelectOption extends SelectOption {
  data: PackageData;
}

function ProductsSection() {
  const dispatch = useDispatch();
  const isEdit = useSelector(selectIsEdit);
  const cartItems = useSelector(selectCartItems);
  const bookingDate = useSelector(selectBookingDate);
  const isDiscountedBooking = useSelector(selectIsDiscountedBooking); 

  const [productOptions, setProductsOptions] = useState<SelectOption[]>([]);
  const [packageOptions, setPackageOptions] = useState<PackageSelectOption[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  // selected package
  const [selectedPackage, setSelectedPackage] = useState<PackageData | null>(null);
  // selected products from package 
  const [selectedProducts, setSelectedProducts] = useState<{ [key: number]: number }>({});

  // console.log('Selected Products from Package:', selectedProducts);
  // console.log('Selected Package:', selectedPackage);

  const isPastBooking = (() => {
    if (!bookingDate || !isEdit) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const booking = new Date(bookingDate);
    booking.setHours(0, 0, 0, 0);

    return booking < today;
  })();

  useEffect(() => {
    if (isEdit && cartItems) {
      const packageItem = cartItems.find(item => item.type === 'package');
      if (packageItem) {
        // Find the matching package from packageOptions
        const foundPackage = packageOptions.find(pkg => pkg.value == packageItem.id)?.data;
        if (foundPackage) {
          setSelectedPackage(foundPackage);
          if (foundPackage.groups) {
            const productSelections: { [key: number]: number } = {};
            
            foundPackage.groups.forEach(group => {
              const selectedProductId = group.products?.[0]?.id
              if (selectedProductId) {
                productSelections[group.id] = selectedProductId;
              }
            });
            
            setSelectedProducts(productSelections);
            // Mark initial load as complete
          }
        }
      }
    }
  }, [isEdit, cartItems, packageOptions]);

  // cleaning deposit for edit booking if any product requires it
  useEffect(() => {
    if (isEdit && cartItems && productOptions) {
      const hasItemsWithDeposit = productOptions.some(item => cartItems.find(x => (x.id === item.value && item?.requires_cleaning_deposit))) || 
      selectedPackage?.groups.some(group => group.products.some(product => product.product_price?.requires_cleaning_deposit)) ||
      (selectedPackage?.direct_products && selectedPackage?.direct_products.some(product => product.product_price?.requires_cleaning_deposit));
      // console.log('Has items with cleaning deposit:', hasItemsWithDeposit);
      dispatch(setCleaningDeposit({hasCleaningDeposit : hasItemsWithDeposit || false}));
    }
  }, [isEdit, productOptions, cartItems, selectedPackage, dispatch]);


  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [products, packages] = await Promise.all([
          getProducts('limit=10000&status=active'),
          getPackages('limit=10000&status=active')
        ]);

        if (products.success) {
          setProductsOptions(products.data.items?.map((product: ProductData) => {
            const productPrice = product.product_price?.is_sale_item === 1 
              ? product.product_price.sale_price 
              : product.product_price.price;
            return {
              value: product.id,
              label: `${product.product_name}${productPrice ? ` - £${productPrice}` : ''}`,
              price: Number(productPrice), 
              requires_cleaning_deposit: product?.product_price.requires_cleaning_deposit
            };
          }));
        }

        if (packages.success) {
          setPackageOptions(packages.data.items?.map((pkg: any) => ({
            value: pkg.id,
            label: `${pkg.package_name} - £${pkg.price}`,
            price: Number(pkg.price),
            requires_cleaning_deposit: pkg.groups.some(group => 
              group.products.some(product => product.product_price?.requires_cleaning_deposit)
            ) || (pkg.direct_products && pkg.direct_products.some(product => product.product_price?.requires_cleaning_deposit)),
            data: pkg
          })));
        }
      } catch (error) {
        console.error('Error fetching options:', error);
      }
      setLoading(false);
    };
    fetchData();
  }, []);

  const formik = useFormik<FormValues>({    
    initialValues: (() => {
      if (!isEdit || !cartItems) {
        return {
          date: new Date(),
          products: [],
          packages: null,
        };
      }

      const packageItem = cartItems.find(item => item.type === 'package');
      const productItems = cartItems.filter(item => !item.type);

      return {
        date: bookingDate ? new Date(bookingDate) : new Date(),
        products: productItems.map(item => ({
          value: item.id,
          label: item.name,
          price: item.price,
          quantity: item.amount,
          requires_cleaning_deposit: item.requires_cleaning_deposit
        })),
        packages: packageItem ? {
          value: packageItem.id,
          label: packageItem.name,
          price: packageItem.price, 
          requires_cleaning_deposit: packageItem.requires_cleaning_deposit, 
        } : null,
      };
    })(),
    validationSchema: yup.object().shape({      
      date: yup.date()
        .min(new Date().toISOString().split('T')[0], 'Booking date cannot be in the past')
        .required('Date is required'),
      products: yup.array().of(
        yup.object().shape({
          value: yup.string().required('Product is required'),
          label: yup.string().required(),
          price: yup.number().required(),
          quantity: yup.number().min(1, 'Quantity must be at least 1').required('Quantity is required')
        })
      )  
    }),
    onSubmit: async (values: any, { setSubmitting }) => {
      if (!values.date || (selectedPackage && !isPackageSelectionValid())) {
        setSubmitting(false);
        return;
      }
      setSubmitting(true);      
      const requestData = {
        booking_date: values.date.toISOString().split('T')[0],
        products: [
          // Products from selected package
          ...(selectedPackage && selectedProducts
            ? Object.values(selectedProducts).map((productId) => ({
                product_id: productId,
                quantity: 1
              }))
            : []),
          // direct products from package
          ...(selectedPackage && selectedPackage.direct_products
            ? selectedPackage.direct_products.map((product: any) => ({
                product_id: product.id,
                quantity: 1
              }))
            : []),
          // Manually selected products
          ...values.products.map((product: ProductWithQuantity) => ({
            product_id: product.value,
            quantity: product.quantity
          }))
        ]
      };

      // check if requestData has duplicate products
      const productIds = requestData.products.map((p: any) => p.product_id);
      // console.log('Product IDs:', productIds);
      const hasDuplicates = productIds.length !== new Set(productIds).size;
      // console.log('Has Duplicates:', hasDuplicates);
      const duplicateProductIds = productIds.filter((item, index) => productIds.indexOf(item) !== index);
      // console.log('Duplicate Product IDs:', duplicateProductIds);
      if (hasDuplicates) {
        let selectedProducts = formik.values.products;  
        let updatedProducts = [...selectedProducts];
        
        // Remove duplicate products from formik
        duplicateProductIds.forEach(duplicateId => {
          const productIndex = updatedProducts.findIndex(product => product.value === duplicateId);
          if (productIndex !== -1) {
            updatedProducts.splice(productIndex, 1);
          }
      });
      // console.log('Updated Products:', updatedProducts);
      // Update formik with products that don't duplicate package products
      formik.setFieldValue('products', updatedProducts);
      
      toast.warning('Product can only be added once to a booking either as a package item or individual product. Duplicated products are removed from selection.');
      setSubmitting(false);
      return;
    }

    const response = await checkAvailability(requestData);
    
    if (response.success) {
      dispatch(setBookingDate(values.date.toISOString().split('T')[0]));          
      if (selectedPackage) {
        // console.log(selectedPackage)
        dispatch(setIsPackageItem(true));
        dispatch(setPackageData(selectedPackage));
        dispatch(setItems([{
          id: selectedPackage.id.toString(),
          name: selectedPackage.package_name,
          amount: 1,
          price: selectedPackage.price,
          type: 'package',
          products: selectedProducts ? [...Object.values(selectedProducts)].map(id => id.toString()) : [],
          requires_cleaning_deposit: selectedPackage.groups.some(group => 
          group.products.some(product => product.product_price?.requires_cleaning_deposit)
        ) || (selectedPackage.direct_products && selectedPackage.direct_products.some(product => product.product_price?.requires_cleaning_deposit)),
        }, ...values.products.map(product => ({
          id: product.value,
          name: product.label,
          amount: Number(product.quantity),
          price: product.price, 
          requires_cleaning_deposit : product.requires_cleaning_deposit,
        }))]));
      } else {
        dispatch(setIsPackageItem(false));
        dispatch(setPackageData(null));
        dispatch(setItems(values.products.map(product => ({
          id: product.value,
          name: product.label,
          amount: Number(product.quantity),
          price: product.price,
          requires_cleaning_deposit : product.requires_cleaning_deposit,
        }))));
      }
      toast.success('Cart updated successfully');
    }
    else{
      const unavailable_products : {item_id : string, available_quantity : number}[] = response.unavailable_products || [];
      const selectedProducts = formik.values.products;
      let updatedProducts = [...selectedProducts]; 

      if (unavailable_products.length > 0) {
        unavailable_products.forEach(element => {
          const productIndex = updatedProducts.findIndex(product => product.value === element.item_id);
          
          if (productIndex !== -1) {
            if (element.available_quantity === 0) {
              // Remove product if quantity is 0
              updatedProducts.splice(productIndex, 1);
            } else if (element.available_quantity > 0) {
              // Update product quantity if quantity > 0
              updatedProducts[productIndex] = {
                ...updatedProducts[productIndex],
                quantity: element.available_quantity
              };
            }
          }
        });
        formik.setFieldValue('products', updatedProducts);
      }
    }
    
      setSubmitting(false);
    }
  });

  const handleProductChange = (selectedProducts: any) => {
    const existingProducts = formik.values.products;
    const productsWithQuantity = (selectedProducts || []).map((product: SelectOption) => {
      const existingProduct = existingProducts.find(p => p.value === product.value);
      return {
        ...product,
        quantity: existingProduct ? existingProduct.quantity : 1
      };
    });
    formik.setFieldValue('products', productsWithQuantity);
  };

  const handleQuantityChange = (productIndex: number, quantity: number) => {
    const newProducts = [...formik.values.products];
    newProducts[productIndex] = { ...newProducts[productIndex], quantity: Math.max(1, quantity) };
    formik.setFieldValue('products', newProducts);
  };  
  
  const handlePackageChange = (selectedOption: any) => {
    // Reset both package product selections and individual product selections
    setSelectedProducts({});
    
    if (selectedOption) {
      setSelectedPackage(selectedOption.data);
      formik.setFieldValue('packages', selectedOption);
    } else {
      setSelectedPackage(null);
      formik.setFieldValue('packages', null);
    }
  };

  const handleGroupProductSelect = (groupId: number, productId: number) => {
    setSelectedProducts(prev => ({
      ...prev,
      [groupId]: productId
    }));
  };

  const isPackageSelectionValid = () => {
    if (!selectedPackage || !selectedPackage.groups || selectedPackage.groups.length === 0) {
      return true;
    }
    
    // Validate all groups have a selection
    for (const group of selectedPackage.groups) {
      const hasSelection = Object.values(selectedProducts).some(productId => 
        group.products.some(p => p.id === productId)
      );
      
      if (!hasSelection) {
        return false;
      }
    }
    
    return true;
  };

  return (
    <form onSubmit={(e) => {
      e.preventDefault();
      formik.handleSubmit(e);
    }}>
      
      <div className="mb-4">
        <label className="form-label">Booking Date</label>
          {/* Make the datepicker a fixed-width item so the button can grow */}
          <div className="d-flex align-items-start">
            {/* DatePicker takes remaining space */}
            <div className="flex-grow-1">
              <DatePicker
                selected={formik.values.date}
                onChange={(date) => formik.setFieldValue('date', date)}
                minDate={new Date()}
                className={`form-control ${formik.touched.date && formik.errors.date ? 'is-invalid' : ''}`}
                dateFormat="MMMM d, yyyy"
                disabled={isDiscountedBooking || isPastBooking}
              />
            </div>

            {/* Conditionally render button */}
            {formik.values.date && bookingDate &&
              formik.values.date.toISOString().split('T')[0] !== bookingDate && (
                <div className="ms-2">
                  {/* Button only takes as much width as needed */}
                  <button
                    onClick={() => { formik.handleSubmit(); }}
                    className="btn btn-light w-auto"
                    type="button"
                    disabled={isDiscountedBooking || isPastBooking}
                  >
                    Check Availability
                  </button>
                </div>
              )
            }
          </div>
        {formik.touched.date && formik.errors.date && (
          <div className="invalid-feedback d-block">{formik.errors.date as string}</div>
        )}
      </div>

      <div className="mb-4">
        <label className="form-label">Package</label>
        <Select<SelectOption, false>
          value={formik.values.packages}
          onChange={handlePackageChange}
          options={packageOptions}
          isLoading={loading}
          placeholder="Select package"
          className="react-select"
          classNamePrefix="select"
          isClearable
          isDisabled={isDiscountedBooking || isPastBooking}
        />
      </div>

      {selectedPackage && selectedPackage.groups && (
        <div className="border rounded p-3 bg-light mb-4">
          <label className="form-label">Package Details</label>
            {selectedPackage.direct_products && selectedPackage.direct_products.length > 0 && (
            <Row>
              <Col md={12} className="mb-3">
                <h6 className="text-muted mb-3">
                  <i className="mdi mdi-package-variant me-2"></i>
                  Included Products
                </h6>
                <div className="row">
                  {selectedPackage.direct_products.map(product => (
                    <div key={product.id} className="col-md-6 mb-2">
                      <div className="d-flex align-items-center p-2 bg-white rounded border-start border-success border-3">
                        <i className="mdi mdi-check-circle text-success me-2"></i>
                        <span className="fw-medium">{product.product_name}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </Col>
            </Row>
            )}
          {selectedPackage.groups.length > 0 && (
            <Row>
              <h6 className="text-muted mb-3">
                <i className="mdi mdi-package-variant me-2"></i>
                Select Products from Groups
              </h6>
              {selectedPackage.groups.map(group => (
              <Col md={12} className="mb-3" key={group.id}>
                <div>
                  <Label>{group.group_name}</Label>
                  <Input
                    type="select"
                    className="form-select"
                    value={selectedProducts[group.id] || ''}
                    onChange={(e) => handleGroupProductSelect(group.id, parseInt(e.target.value))}
                    disabled={isDiscountedBooking || isPastBooking}
                  >
                    <option value="">Select a product</option>
                    {group.products.map(product => (
                      <option key={product.id} value={product.id}>
                        {product.product_name}
                      </option>
                    ))}
                  </Input>
                </div>
              </Col>
            ))}
          </Row>)}
        </div>
      )}

      <div className="mb-4">
        <label className="form-label">Products</label>
        <Select<SelectOption, true>
          isMulti
          value={formik.values.products}
          onChange={handleProductChange}
          options={productOptions}
          isLoading={loading}
          placeholder="Select products"
          className={`react-select ${
            formik.touched.products && formik.errors.products ? 'is-invalid' : ''
          }`}
          classNamePrefix="select"
          isDisabled={isDiscountedBooking || isPastBooking}
        />
        {formik.touched.products && formik.errors.products && (
          <div className="invalid-feedback d-block">
            {typeof formik.errors.products === 'string' ? formik.errors.products : 'Please select at least one product'}
          </div>
        )}
      </div>

      {formik.values.products.length > 0 && (
        <div className="mb-4">
          <label className="form-label">Quantities</label>
          {formik.values.products.map((product, index) => (
            <div key={product.value} className="d-flex align-items-center mb-2">
              <span className="me-3">{product.label}</span>
              <Input
                type="number"
                min="1"
                value={product.quantity}
                onChange={(e) => handleQuantityChange(index, parseInt(e.target.value))}
                className="w-auto"
                style={{ maxWidth: '100px' }}
                disabled={isDiscountedBooking || isPastBooking}
              />
            </div>
          ))}
        </div>
      )}
      
      {!(isDiscountedBooking || isPastBooking) && 
        <div className="d-flex justify-content-end align-items-center">        
        <Button color="primary" type="button" 
          disabled={
            formik.isSubmitting || 
            !formik.isValid || 
            (formik.values.products.length === 0 && !selectedPackage) ||
            (selectedPackage && !isPackageSelectionValid()) || 
            isDiscountedBooking || isPastBooking
          }
          onClick={() => {formik.handleSubmit();}}
        >
          {formik.isSubmitting ? 'Adding...' : 'Add Items to cart'}
        </Button>
      </div>}

      {isDiscountedBooking &&
      <Alert color="warning" className="mt-4">
        <strong>Note:</strong> Edit for discounted bookings is not allowed.
      </Alert>}
      {isPastBooking &&
      <Alert color="warning" className="mt-4">
        <strong>Note:</strong> You can not edit past booking items.
      </Alert>}
    </form>
  );
}

export default ProductsSection;
